import { coreReleaseCandidates, supportedCoreVersions } from '@emma/core/core';
import { Box, Button, Dropdown, FormControl, Modal } from '@lego/klik-ui';
import { type ChangeEvent, type DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { UploadDropZone } from '@/components/file/upload/UploadDropZone';
import { UploadProgressBar } from '@/components/file/upload/UploadProgressBar';
import { getApiErrorDetail } from '@/hooks/api';
import { useAddApplicationVersion } from '@/hooks/application_version';
import { useFileUpload } from '@/hooks/common/useFileUpload';
import { validateDockerCompose } from './validateDockerCompose';

const FALLBACK_ERROR = 'Failed to create application version';

type Props = {
  workspaceId: number;
  applicationId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const availableCoreVersions = [...supportedCoreVersions, ...coreReleaseCandidates];

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.readAsDataURL(file);
  });
}

export const CreateApplicationVersionDialog = ({
  workspaceId,
  applicationId,
  isOpen,
  onClose,
  onSuccess,
}: Props) => {
  const [coreVersion, setCoreVersion] = useState<string>(availableCoreVersions[0]);
  // The mutation's onError callback fires before handleSubmit's catch block,
  // so we stash the API detail in a ref for both branches to read.
  const lastErrorDetail = useRef<string | undefined>(undefined);
  const onApiError = useCallback((error?: unknown) => {
    lastErrorDetail.current = getApiErrorDetail(error);
  }, []);
  const addVersion = useAddApplicationVersion(workspaceId, applicationId, onApiError);
  const upload = useFileUpload({
    onSuccess: () => {
      onClose();
      onSuccess?.();
    },
  });

  useEffect(() => {
    if (isOpen) {
      upload.reset();
      setCoreVersion(availableCoreVersions[0]);
    }
  }, [isOpen, upload.reset]);

  const handleSubmit = async (selectedFile: File) => {
    upload.startUpload();
    lastErrorDetail.current = undefined;
    try {
      const base64 = await readFileAsBase64(selectedFile);
      await addVersion({
        composePayload: base64,
        coreVersion,
      });
      upload.finishUpload();
    } catch {
      upload.failUpload(lastErrorDetail.current ?? FALLBACK_ERROR);
    }
  };

  const processFile = async (selectedFile: File) => {
    const name = selectedFile.name.toLowerCase();
    if (!name.endsWith('.yml') && !name.endsWith('.yaml')) {
      upload.setValidationError(selectedFile, 'Please select a YAML file (.yml or .yaml)');
      return;
    }

    let text: string;
    try {
      text = await selectedFile.text();
    } catch {
      upload.setValidationError(selectedFile, 'Failed to read file');
      return;
    }

    const result = validateDockerCompose(text);
    if (!result.valid) {
      const message =
        result.errors.length === 1
          ? result.errors[0]
          : result.errors.map((e) => `- ${e}`).join('\n');
      upload.setValidationError(selectedFile, message);
      return;
    }

    upload.setFile(selectedFile);
    void handleSubmit(selectedFile);
  };

  const uploadFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    void processFile(files[0]);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    upload.setIsInDropArea(false);
    uploadFiles(e.dataTransfer.files);
  };
  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    uploadFiles(e.target.files);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>Create Application Version</Modal.Header>
        <Modal.CloseButton />
        <Modal.Body>
          <FormControl mb={4}>
            <FormControl.Label>Core Version</FormControl.Label>
            <Dropdown onChange={(value: string) => setCoreVersion(value)} value={[coreVersion]}>
              {availableCoreVersions.map((v) => (
                <Dropdown.Option key={v} value={v}>
                  {v}
                </Dropdown.Option>
              ))}
            </Dropdown>
          </FormControl>
          <FormControl>
            <FormControl.Label>Docker Compose File</FormControl.Label>
            <Box
              alignItems="center"
              display="flex"
              justifyContent="center"
              minHeight="150px"
              textAlign="center"
            >
              {upload.uploadStatus === 'idle' && (
                <UploadDropZone
                  isInDropArea={upload.isInDropArea}
                  onDragEnter={upload.handleDragEnter}
                  onDragLeave={upload.handleDragLeave}
                  onDragOver={upload.handleDragOver}
                  onDrop={handleDrop}
                  onFileSelect={handleFileSelect}
                />
              )}
              {(upload.uploadStatus === 'uploading' ||
                upload.uploadStatus === 'success' ||
                upload.uploadStatus === 'error') && (
                <UploadProgressBar
                  errorMessage={upload.errorMessage}
                  fileName={upload.file?.name}
                  onGoBack={upload.uploadStatus === 'error' ? upload.reset : undefined}
                  progress={upload.progress}
                  status={upload.uploadStatus}
                />
              )}
            </Box>
          </FormControl>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
